import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import { ExternalLink, Loader2, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import TabPageLayout from './TabPageLayout';
import { orgApi } from '../utils/apiV2';
import GoogleAdsSetupGuide from './GoogleAdsSetupGuide';

const fetcher = async () => {
  const response = await orgApi.getOrganization();
  return response.data;
};

const ConversionsTab = () => {
  const [saving, setSaving] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [settings, setSettings] = useState({
    googleAdsId: '',
    googleAdsLabel: '',
    metaPixelId: '',
    ga4MeasurementId: '',
  });
  const [hasChanges, setHasChanges] = useState(false);

  const { data: orgData, isLoading, mutate } = useSWR(
    'organization-settings',
    fetcher,
    { revalidateOnFocus: true }
  );

  useEffect(() => {
    if (orgData) {
      setSettings({
        googleAdsId: orgData.googleAdsId || orgData.google_ads_id || '',
        googleAdsLabel: orgData.googleAdsLabel || orgData.google_ads_label || '',
        metaPixelId: orgData.metaPixelId || orgData.meta_pixel_id || '',
        ga4MeasurementId: orgData.ga4MeasurementId || orgData.google_analytics_id || '',
      });
      setHasChanges(false);
    }
  }, [orgData]);

  const handleChange = (field, value) => {
    const sanitized = value.replace(/[^a-zA-Z0-9\-_]/g, '');
    setSettings(prev => ({ ...prev, [field]: sanitized }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Auto-prefix AW- if they forgot it
      let adsId = settings.googleAdsId.trim();
      if (adsId && !adsId.startsWith('AW-')) {
        adsId = `AW-${adsId}`;
      }
      
      await orgApi.updateOrganization({
        googleAdsId: adsId || null,
        googleAdsLabel: settings.googleAdsLabel.trim() || null,
        metaPixelId: settings.metaPixelId.trim() || null,
        ga4MeasurementId: settings.ga4MeasurementId.trim() || null,
      });
      toast.success('Settings saved!');
      setHasChanges(false);
      mutate();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (showGuide) {
    return <GoogleAdsSetupGuide onBack={() => setShowGuide(false)} />;
  }

  return (
    <TabPageLayout 
      title="Conversions" 
      subtitle="Track ad conversions from bookings"
      loading={isLoading}
      actions={
        <Button 
          onClick={handleSave} 
          disabled={saving || !hasChanges}
          className="bg-blue-600 hover:bg-blue-700 text-white h-10"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Save
        </Button>
      }
    >
      <div className="max-w-xl space-y-6">
        {/* Google Ads */}
        <div className="bg-white rounded-xl p-5 shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0014.07 6H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" fill="#4285F4"/>
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900">Google Ads</h3>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="googleAdsId" className="text-sm">Conversion ID</Label>
              <Input
                id="googleAdsId"
                value={settings.googleAdsId}
                onChange={(e) => handleChange('googleAdsId', e.target.value)}
                placeholder="AW-123456789"
                className="font-mono mt-1"
                maxLength={20}
              />
            </div>
            <div>
              <Label htmlFor="googleAdsLabel" className="text-sm">Conversion Label</Label>
              <Input
                id="googleAdsLabel"
                value={settings.googleAdsLabel}
                onChange={(e) => handleChange('googleAdsLabel', e.target.value)}
                placeholder="AbCdEfGhIjKlMnOp"
                className="font-mono mt-1"
                maxLength={30}
              />
            </div>
          </div>

          <button 
            onClick={() => setShowGuide(true)}
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium mt-3"
            data-testid="open-google-ads-guide"
          >
            <HelpCircle className="w-4 h-4" />
            How to find your Conversion ID & Label
          </button>
        </div>

        {/* GA4 */}
        <div className="bg-white rounded-xl p-5 shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center">
              <svg className="w-5 h-5" viewBox="0 0 192 192" fill="none">
                <path d="M96 16C64 16 32 48 32 80c0 24 16 44 40 52-4-8-6-16-6-24 0-20 14-38 30-50 0 20 10 36 24 46 4-10 6-20 6-32 8 12 14 26 14 42 0 12-4 24-10 34 24-8 40-28 40-52 0-48-40-80-74-80z" fill="#F57C00"/>
                <path d="M96 96c-12 8-20 22-20 38 0 20 16 36 36 36s36-16 36-36c0-28-24-50-52-38z" fill="#FF9800"/>
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900">Google Analytics 4</h3>
          </div>

          <div>
            <Label htmlFor="ga4MeasurementId" className="text-sm">Measurement ID</Label>
            <Input
              id="ga4MeasurementId"
              value={settings.ga4MeasurementId}
              onChange={(e) => handleChange('ga4MeasurementId', e.target.value)}
              placeholder="G-XXXXXXXXXX"
              className="font-mono mt-1"
              maxLength={20}
              data-testid="ga4-measurement-id-input"
            />
          </div>

          <a
            href="https://support.google.com/analytics/answer/9304153"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-3"
          >
            <ExternalLink className="w-3 h-3" />
            How to find your Measurement ID
          </a>
        </div>

        {/* Meta Pixel */}
        <div className="bg-white rounded-xl p-5 shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2"/>
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900">Meta Pixel</h3>
          </div>

          <div>
            <Label htmlFor="metaPixelId" className="text-sm">Pixel ID</Label>
            <Input
              id="metaPixelId"
              value={settings.metaPixelId}
              onChange={(e) => handleChange('metaPixelId', e.target.value)}
              placeholder="1234567890123456"
              className="font-mono mt-1"
              maxLength={20}
            />
          </div>

          <a 
            href="https://www.facebook.com/business/help/952192354843755" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-3"
          >
            <ExternalLink className="w-3 h-3" />
            Find your Pixel ID
          </a>
        </div>

        {/* Test Setup */}
        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200 space-y-4">
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-0.5">Verify your setup</p>
            <p className="text-sm text-gray-500">
              Complete a test booking, then use these tools to confirm events are firing correctly.
            </p>
          </div>

          {/* Google Ads + GA4 */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Google Ads &amp; GA4</p>
            <div className="flex flex-wrap gap-2">
              <a
                href="https://tagassistant.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#4285F4">
                  <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0014.07 6H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z"/>
                </svg>
                Google Tag Assistant
                <span className="text-xs text-gray-400">Chrome ext</span>
              </a>
              <a
                href="https://analytics.google.com/analytics/web/#/p/debugview"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 192 192" fill="none">
                  <path d="M96 16C64 16 32 48 32 80c0 24 16 44 40 52-4-8-6-16-6-24 0-20 14-38 30-50 0 20 10 36 24 46 4-10 6-20 6-32 8 12 14 26 14 42 0 12-4 24-10 34 24-8 40-28 40-52 0-48-40-80-74-80z" fill="#F57C00"/>
                  <path d="M96 96c-12 8-20 22-20 38 0 20 16 36 36 36s36-16 36-36c0-28-24-50-52-38z" fill="#FF9800"/>
                </svg>
                GA4 DebugView
                <span className="text-xs text-gray-400">real-time events</span>
              </a>
            </div>
          </div>

          {/* Meta */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Meta Pixel</p>
            <div className="flex flex-wrap gap-2">
              <a
                href="https://chrome.google.com/webstore/detail/meta-pixel-helper/fdgfkebogiimcoedlicjlajpkdmockpc"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#1877F2">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Meta Pixel Helper
                <span className="text-xs text-gray-400">Chrome ext</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </TabPageLayout>
  );
};

export default ConversionsTab;
